import { ArrowDownToLine, RotateCw } from "lucide-react";
import type { FC } from "react";
import React, { useState } from "react";

import { Button } from "@react/ui-primitives/button";

import type { LogEntry } from "@apis/calorie-grid/models";

import { GRID_COLUMNS } from "../../../bootstrap/grid.ts";
import { useConfig } from "../../../shared/contexts/config/useConfig.ts";
import { shapeManager } from "../../../shared/lib/ShapeManager.ts";
import { usePlacementQueueActions } from "../hooks/usePlacementQueueActions.ts";

import { ShapePreview } from "./ShapePreview.tsx";

interface ShapePickerProps {
  readonly logEntry: LogEntry;
}

export const ShapePicker: FC<ShapePickerProps> = ({ logEntry }) => {
  const [shape, setShape] = useState<number>();

  const { squareKcal } = useConfig();
  const totalSquares = shapeManager.squareCount(
    logEntry.totalCalories,
    squareKcal,
  );

  const squaresPerRow = shape ?? Math.min(totalSquares, GRID_COLUMNS);

  const { drop } = usePlacementQueueActions();

  const formattedCalories = logEntry.totalCalories.toLocaleString();
  const summaryLabel = `${formattedCalories} kcal · ${totalSquares} sq`;

  const handleDrop = (): void => {
    drop.mutate({ logEntry, squaresPerRow });
  };

  const handleShapeChange = (): void => {
    setShape(
      shapeManager.nextSquaresPerRow(squaresPerRow, totalSquares, GRID_COLUMNS),
    );
  };

  return (
    <div className="flex items-center gap-3">
      <ShapePreview
        category={logEntry.category}
        squaresPerRow={squaresPerRow}
        totalSquares={totalSquares}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-semibold">{logEntry.name}</span>
        <span className="truncate font-mono text-[11.5px] text-muted-foreground">
          {summaryLabel}
        </span>
      </div>
      <Button
        aria-label="Change shape"
        onClick={handleShapeChange}
        size="icon"
        variant="outline"
      >
        <RotateCw className="size-4" />
      </Button>
      <Button disabled={drop.isPending} onClick={handleDrop}>
        <ArrowDownToLine className="size-4" />
        Drop
      </Button>
    </div>
  );
};
